const RE = /in this article/i;
const HIDE = "blog-post-toc__source--hidden";
const BAD =
  "h1,h2,h3,h4,h5,h6,figure,img,iframe,video,table,.full-width-padding,.TwoColumns__Wrapper,.large2,.LeftBarPage__ArticleTitle";

/** @param {HTMLElement} n */
const hide = (n) => {
  n.classList.add(HIDE);
  n.hidden = true;
  n.setAttribute("aria-hidden", "true");
};

/** @param {HTMLElement} rte */
function findToc(rte) {
  /** @param {Element} el */
  const introOk = (el) => {
    const t = el.textContent ?? "";
    return RE.test(t) && t.search(RE) <= 200 && t.length <= 4000;
  };
  /** @param {Element} el */
  const hasLinks = (el) =>
    !!el.querySelector("a") ||
    (el.nextElementSibling instanceof HTMLElement &&
      el.nextElementSibling.matches("ul, ol") &&
      !!el.nextElementSibling.querySelector("a"));
  /** @param {HTMLElement} el */
  const tocOnly = (el) => {
    const t = (el.textContent ?? "").trim();
    return (
      RE.test(t) &&
      t.length <= 2500 &&
      !el.querySelector(BAD) &&
      !!el.querySelector("a")
    );
  };
  /** @param {HTMLElement} parent @param {HTMLElement} intro */
  const hasPrior = (parent, intro) => {
    for (const c of parent.children) {
      if (c === intro) break;
      if (
        c.querySelector(BAD) ||
        ((c.textContent ?? "").trim() && !RE.test(c.textContent ?? ""))
      )
        return true;
    }
    return false;
  };
  /** @param {HTMLElement} intro */
  const pack = (intro) => {
    const parent = intro.parentElement;
    if (
      parent instanceof HTMLElement &&
      tocOnly(parent) &&
      !hasPrior(parent, intro)
    )
      return { nodes: [parent], anchors: [...parent.querySelectorAll("a")] };
    const nodes = [intro];
    let anchors = [...intro.querySelectorAll("a")];
    const sib = intro.nextElementSibling;
    if (
      sib instanceof HTMLElement &&
      (sib.matches("ul, ol") || (sib.querySelector("a") && tocOnly(sib)))
    ) {
      nodes.push(sib);
      anchors = anchors.concat([...sib.querySelectorAll("a")]);
    }
    return anchors.length ? { nodes, anchors } : null;
  };
  /** @param {HTMLElement} el */
  const tryHost = (el) => (introOk(el) && hasLinks(el) ? pack(el) : null);

  for (const el of rte.querySelectorAll("p")) {
    const block = tryHost(/** @type {HTMLElement} */ (el));
    if (block) return block;
  }
  for (const el of rte.querySelectorAll("strong, b, h2, h3, h4")) {
    if (!introOk(el)) continue;
    const host = el.closest("p, li, div");
    const block = host instanceof HTMLElement ? tryHost(host) : null;
    if (block) return block;
  }
  return null;
}

class BlogPostToc extends HTMLElement {
  /** @type {(() => void) | null} */
  #offDoc = null;
  /** @type {((e: KeyboardEvent) => void) | null} */
  #offKey = null;
  #done = false;

  connectedCallback() {
    customElements
      .whenDefined("rte-formatter")
      .then(() => requestAnimationFrame(() => this.#init()));
  }

  disconnectedCallback() {
    this.#offDoc?.();
    this.#offDoc = null;
    if (this.#offKey) document.removeEventListener("keydown", this.#offKey);
    this.#offKey = null;
  }

  #init() {
    if (this.#done) return;
    const rte = this.closest('[id^="shopify-section"]')?.querySelector(
      ".blog-post-content rte-formatter",
    );
    const toc = rte ? findToc(/** @type {HTMLElement} */ (rte)) : null;
    const list = this.querySelector(".blog-post-toc__list");
    const panel = this.querySelector(".blog-post-toc__panel");
    const btn = this.querySelector("[data-blog-post-toc-trigger]");
    if (
      !toc?.anchors.length ||
      !(list instanceof HTMLUListElement) ||
      !(panel instanceof HTMLElement) ||
      !(btn instanceof HTMLButtonElement)
    ) {
      this.hidden = true;
      return;
    }

    const seen = new Set();
    for (const a of toc.anchors) {
      const key = a.getAttribute("href") ?? a.textContent?.trim() ?? "";
      if (seen.has(key)) continue;
      seen.add(key);
      const li = document.createElement("li");
      li.className = "blog-post-toc__item";
      li.append(a.cloneNode(true));
      list.append(li);
    }
    if (!list.childElementCount) {
      this.hidden = true;
      return;
    }
    toc.nodes.forEach(hide);

    const close = () => {
      panel.hidden = true;
      btn.setAttribute("aria-expanded", "false");
      this.#offDoc?.();
      this.#offDoc = null;
    };

    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const open = panel.hidden;
      this.#offDoc?.();
      panel.hidden = !open;
      btn.setAttribute("aria-expanded", String(open));
      if (!open) return;
      /** @param {MouseEvent} ev */
      const onDoc = (ev) => {
        if (!this.contains(/** @type {Node} */ (ev.target))) close();
      };
      this.#offDoc = () => document.removeEventListener("click", onDoc);
      queueMicrotask(() => document.addEventListener("click", onDoc));
    });

    this.#offKey = (e) => {
      if (e.key === "Escape" && !panel.hidden) close();
    };
    document.addEventListener("keydown", this.#offKey);
    this.#done = true;
  }
}

if (!customElements.get("blog-post-toc"))
  customElements.define("blog-post-toc", BlogPostToc);
